import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import ErrorBoundary from './components/ErrorBoundary';
import CookieConsent from './components/CookieConsent';

// Lazy load pages for better performance
const LandingPage = lazy(() => import('./unAuth/LandingPage'));
const ContactPage = lazy(() => import('./unAuth/ContactPage'));
const AboutPage = lazy(() => import('./unAuth/AboutPage'));
const PrivacyPage = lazy(() => import('./unAuth/PrivacyPage'));
const TermsPage = lazy(() => import('./unAuth/TermsPage'));
const FAQPage = lazy(() => import('./unAuth/FAQPage'));
const JPGToPNGPage = lazy(() => import('./unAuth/JPGToPNGPage'));
const PNGToJPGPage = lazy(() => import('./unAuth/PNGToJPGPage'));
const PNGToWEBPPage = lazy(() => import('./unAuth/PNGToWEBPPage'));
const WEBPToPNGPage = lazy(() => import('./unAuth/WEBPToPNGPage'));
const JPGToWEBPPage = lazy(() => import('./unAuth/JPGToWEBPPage'));
const WEBPToJPGPage = lazy(() => import('./unAuth/WEBPToJPGPage'));
const HEICToJPGPage = lazy(() => import('./unAuth/HEICToJPGPage'));
const HEICToPNGPage = lazy(() => import('./unAuth/HEICToPNGPage'));
const SVGToPNGPage = lazy(() => import('./unAuth/SVGToPNGPage'));
const SVGToJPGPage = lazy(() => import('./unAuth/SVGToJPGPage'));
const PNGToSVGPage = lazy(() => import('./unAuth/PNGToSVGPage'));
const PDFToJPGPage = lazy(() => import('./unAuth/PDFToJPGPage'));
const PDFToPNGPage = lazy(() => import('./unAuth/PDFToPNGPage'));
const JPGToPDFPage = lazy(() => import('./unAuth/JPGToPDFPage'));
const PNGToPDFPage = lazy(() => import('./unAuth/PNGToPDFPage'));
const GIFToMP4Page = lazy(() => import('./unAuth/GIFToMP4Page'));
const MP4ToGIFPage = lazy(() => import('./unAuth/MP4ToGIFPage'));
const JPGvsPNGGuide = lazy(() => import('./unAuth/guides/JPGvsPNGGuide'));
const WhatIsWEBPGuide = lazy(() => import('./unAuth/guides/WhatIsWEBPGuide'));
const HowToCompressImagesGuide = lazy(() => import('./unAuth/guides/HowToCompressImagesGuide'));
const HowToResizeImagesGuide = lazy(() => import('./unAuth/guides/HowToResizeImagesGuide'));
const ImageFormatComparisonGuide = lazy(() => import('./unAuth/guides/ImageFormatComparisonGuide'));
const BestPracticesForWebImagesGuide = lazy(() => import('./unAuth/guides/BestPracticesForWebImagesGuide'));
const ImageOptimizationTechniquesGuide = lazy(() => import('./unAuth/guides/ImageOptimizationTechniquesGuide'));
const ResizePage = lazy(() => import('./unAuth/ResizePage'));
const CompressPage = lazy(() => import('./unAuth/CompressPage'));
const CropPage = lazy(() => import('./unAuth/CropPage'));
const RotatePage = lazy(() => import('./unAuth/RotatePage'));
const BackgroundRemovalPage = lazy(() => import('./unAuth/BackgroundRemovalPage'));
const MetadataPage = lazy(() => import('./unAuth/MetadataPage'));

// Loading component
const LoadingFallback = () => (
  <div style={{
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column'
  }}>
    <div style={{
      width: '50px',
      height: '50px',
      border: '4px solid #f3f3f3',
      borderTop: '4px solid #007bff',
      borderRadius: '50%',
      animation: 'spin 1s linear infinite',
      marginBottom: '20px'
    }}></div>
    <p style={{ color: '#666' }}>Loading...</p>
    <style>{`
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `}</style>
  </div>
);

function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <Router>
        <Suspense fallback={<LoadingFallback />}>
        <Routes>
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/privacy" element={<PrivacyPage />} />
          <Route path="/terms" element={<TermsPage />} />
          <Route path="/faq" element={<FAQPage />} />
          <Route path="/jpg-to-png" element={<JPGToPNGPage />} />
          <Route path="/png-to-jpg" element={<PNGToJPGPage />} />
          <Route path="/png-to-webp" element={<PNGToWEBPPage />} />
          <Route path="/webp-to-png" element={<WEBPToPNGPage />} />
          <Route path="/jpg-to-webp" element={<JPGToWEBPPage />} />
          <Route path="/webp-to-jpg" element={<WEBPToJPGPage />} />
          <Route path="/heic-to-jpg" element={<HEICToJPGPage />} />
          <Route path="/heic-to-png" element={<HEICToPNGPage />} />
          <Route path="/svg-to-png" element={<SVGToPNGPage />} />
          <Route path="/svg-to-jpg" element={<SVGToJPGPage />} />
          <Route path="/png-to-svg" element={<PNGToSVGPage />} />
          <Route path="/pdf-to-jpg" element={<PDFToJPGPage />} />
          <Route path="/pdf-to-png" element={<PDFToPNGPage />} />
          <Route path="/jpg-to-pdf" element={<JPGToPDFPage />} />
          <Route path="/png-to-pdf" element={<PNGToPDFPage />} />
          <Route path="/gif-to-mp4" element={<GIFToMP4Page />} />
          <Route path="/mp4-to-gif" element={<MP4ToGIFPage />} />
          <Route path="/guides/jpg-vs-png" element={<JPGvsPNGGuide />} />
          <Route path="/guides/what-is-webp" element={<WhatIsWEBPGuide />} />
          <Route path="/guides/how-to-compress-images" element={<HowToCompressImagesGuide />} />
          <Route path="/guides/how-to-resize-images" element={<HowToResizeImagesGuide />} />
          <Route path="/guides/image-format-comparison" element={<ImageFormatComparisonGuide />} />
          <Route path="/guides/best-practices-for-web-images" element={<BestPracticesForWebImagesGuide />} />
          <Route path="/guides/image-optimization-techniques" element={<ImageOptimizationTechniquesGuide />} />
          <Route path="/resize" element={<ResizePage />} />
          <Route path="/compress" element={<CompressPage />} />
          <Route path="/crop" element={<CropPage />} />
          <Route path="/rotate" element={<RotatePage />} />
          <Route path="/remove-background" element={<BackgroundRemovalPage />} />
          <Route path="/strip-metadata" element={<MetadataPage />} />
        </Routes>
        </Suspense>
      </Router>
      <CookieConsent />
    </div>
    </ErrorBoundary>
  );
}

export default App;
